import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import { openApi } from "../../services/api";
import sestedCursosLogo from "../../assets/sested-cursos-logo.jpg";

const useStyles = makeStyles(theme => ({
  page: {
    minHeight: "100vh",
    background: "#f4f7fa",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(3)
  },
  card: {
    padding: theme.spacing(4),
    borderRadius: 8,
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(3)
    }
  },
  brandHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      marginBottom: theme.spacing(2)
    }
  },
  brandLogo: {
    width: 180,
    height: 70,
    objectFit: "contain",
    borderRadius: 6,
    background: "#ffffff",
    display: "block",
    [theme.breakpoints.down("xs")]: {
      width: 170,
      height: 66
    }
  },
  brandName: {
    color: "#005c91",
    fontWeight: 700,
    lineHeight: 1.1
  },
  formTitle: {
    fontWeight: 700,
    lineHeight: 1.1,
    marginBottom: theme.spacing(0.75)
  },
  formDescription: {
    marginBottom: theme.spacing(1)
  },
  field: {
    marginTop: theme.spacing(2)
  },
  success: {
    textAlign: "center",
    padding: theme.spacing(4)
  }
}));

const PublicLeadForm = () => {
  const classes = useStyles();
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadForm = async () => {
      try {
        const { data } = await openApi.get(`/lead-forms/public/${token}`);
        setForm(data);
        const initialValues = {};
        (data.fields || []).forEach(field => {
          initialValues[field.key] = "";
        });
        setValues(initialValues);
      } catch (err) {
        setError(err.response?.data?.error || "Formulário indisponível.");
      } finally {
        setLoading(false);
      }
    };

    loadForm();
  }, [token]);

  const handleChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const submit = async event => {
    event.preventDefault();
    setSending(true);
    try {
      await openApi.post(`/lead-forms/public/${token}/submissions`, { values });
      setSent(true);
      toast.success("Cadastro recebido com sucesso.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Não foi possível enviar o cadastro.");
    } finally {
      setSending(false);
    }
  };

  const renderField = field => {
    const multiline = field.type === "textarea";
    const inputType =
      field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text";

    return (
      <TextField
        key={field.key}
        className={classes.field}
        label={field.label}
        value={values[field.key] || ""}
        onChange={event => handleChange(field.key, event.target.value)}
        required={Boolean(field.required)}
        type={inputType}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        variant="outlined"
        fullWidth
      />
    );
  };

  const BrandHeader = () => (
    <Box className={classes.brandHeader}>
      <img
        className={classes.brandLogo}
        src={sestedCursosLogo}
        alt="SESTED Cursos"
      />
      <Typography className={classes.brandName} variant="h5">
        SESTED CURSOS
      </Typography>
    </Box>
  );

  return (
    <Box className={classes.page}>
      <Container maxWidth="sm">
        <Paper className={classes.card}>
          {loading && (
            <Box textAlign="center">
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Box className={classes.success}>
              <Typography variant="h5" color="error">{error}</Typography>
            </Box>
          )}

          {!loading && !error && sent && (
            <Box className={classes.success}>
              <BrandHeader />
              <Typography variant="h6">Cadastro recebido com sucesso.</Typography>
              <Typography variant="body2" color="textSecondary">
                Em breve o atendimento responsável dará continuidade.
              </Typography>
            </Box>
          )}

          {!loading && !error && !sent && form && (
            <form onSubmit={submit}>
              <BrandHeader />
              <Typography className={classes.formTitle} variant="h4">{form.title}</Typography>
              {form.description && (
                <Typography className={classes.formDescription} variant="body1" color="textSecondary">
                  {form.description}
                </Typography>
              )}

              {(form.fields || []).map(renderField)}

              <Box mt={3}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={sending}
                >
                  {sending ? "Enviando..." : "Enviar cadastro"}
                </Button>
              </Box>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicLeadForm;
